"use client";

import { useState } from "react";
import {
  Thermometer,
  MapPin,
  Calendar,
  Cloud,
  CloudRain,
  CloudRainWind,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudSun,
  Sun,
  Snowflake,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronUp,
  BarChart3,
  History,
} from "lucide-react";
import { ContextAnalysis, WeatherData } from "@/hooks/useAIContextAnalysis";
import { useWeatherData, getWeatherIcon, type WeatherAPIData } from "@/hooks/useWeatherData";

interface Market {
  id: string;
  question: string;
  outcomes: string[];
  outcomePrices: number[];
}

interface WeatherAISidebarProps {
  analysis: ContextAnalysis;
  eventTitle: string;
  markets?: Market[];
}

// Map icon name string to Lucide component
const WEATHER_ICONS: Record<string, React.ComponentType<{ className?: string; size?: number }>> = {
  Sun,
  CloudSun,
  Cloud,
  CloudFog,
  CloudDrizzle,
  CloudRain,
  CloudRainWind,
  Snowflake,
  CloudLightning,
};

function WeatherIcon({ code, className, size = 16 }: { code: number; className?: string; size?: number }) {
  const iconName = getWeatherIcon(code);
  const Icon = WEATHER_ICONS[iconName] || Cloud;
  return <Icon className={className} size={size} />;
}

function getOutcomePrice(question: string, markets?: Market[]): number | null {
  if (!markets) return null;
  const qLower = question.toLowerCase();
  for (const market of markets) {
    const mLower = market.question.toLowerCase();
    if (mLower.includes(qLower) || qLower.includes(mLower)) {
      // Return the "Yes" outcome price (index 0)
      if (market.outcomePrices[0] != null) {
        return market.outcomePrices[0];
      }
    }
    // Also try matching by outcomes
    const idx = market.outcomes.findIndex((o) => o.toLowerCase().includes(qLower));
    if (idx !== -1 && market.outcomePrices[idx] != null) {
      return market.outcomePrices[idx];
    }
  }
  return null;
}

function findRangeForValue(value: number, ranges: WeatherData["ranges"]): number {
  for (let i = 0; i < ranges.length; i++) {
    const range = ranges[i];
    const min = range.min ?? -Infinity;
    const max = range.max ?? Infinity;
    if (value >= min && value <= max) return i;
  }
  return -1;
}

export default function WeatherAISidebar({
  analysis,
  eventTitle,
  markets = [],
}: WeatherAISidebarProps) {
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [showReasoning, setShowReasoning] = useState(false);

  const weather = analysis.weatherData;
  if (!weather) return null;

  const { data: liveData, loading: weatherLoading, error: weatherError } = useWeatherData(weather);

  const ranges = weather.ranges || [];
  const unit = weather.unit === "F" ? "°F" : "°C";

  // Determine which range the forecast/actual falls into
  const forecastValue = liveData?.forecast?.temperatureMax ?? null;
  const forecastRangeIdx = forecastValue !== null ? findRangeForValue(forecastValue, ranges) : -1;

  // Determine which range the historical average falls into
  const avgMax = liveData?.historical?.averageMax ?? null;
  const avgRangeIdx = avgMax !== null ? findRangeForValue(avgMax, ranges) : -1;

  const historicalYears = liveData?.historical?.years || [];
  const displayedHistory = showAllHistory ? historicalYears : historicalYears.slice(0, 3);

  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 dark:border-[#1A1A1A]">
        <div className="flex items-center gap-2 mb-1">
          <Thermometer className="text-gray-400" size={20} />
          <h3 className="text-gray-900 dark:text-white font-semibold text-base">
            {weather.metricLabel || "Weather Forecast"}
          </h3>
        </div>
        <div className="flex items-center gap-1.5 text-gray-400 text-xs">
          <MapPin size={12} />
          <span>{weather.location.city}, {weather.location.country}</span>
        </div>

        {/* Current temperature */}
        {liveData?.current && (
          <div className="mt-3 flex items-center gap-3">
            <div className="flex items-center gap-2">
              <WeatherIcon code={liveData.current.weatherCode} className="text-sky-400" size={24} />
              <span className="text-2xl font-bold text-gray-900 dark:text-white">
                {Math.round(liveData.current.temperature)}{unit}
              </span>
            </div>
            <span className="text-xs text-gray-400">
              Now &middot; {liveData.current.weatherLabel}
            </span>
          </div>
        )}

        {weatherLoading && !liveData && (
          <div className="mt-3 flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-gray-400">Loading weather data...</span>
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Forecast/Actual for Target Date */}
        {liveData?.forecast && (
          <div className="bg-gray-50 dark:bg-[#18181D] rounded-[5px] p-3 border border-gray-200 dark:border-[#1A1A1A]">
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={14} className="text-gray-400" />
              <span className="text-xs text-gray-400 font-medium">
                {liveData.dataAge === "past" ? "Actual" : "Forecast"} &middot; {formatDate(weather.targetDate)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <WeatherIcon code={liveData.forecast.weatherCode} className="text-gray-300" size={18} />
                <div>
                  <div className="flex items-center gap-1.5">
                    <TrendingUp size={12} className="text-red-400" />
                    <span className="text-gray-900 dark:text-white font-bold text-sm">
                      {Math.round(liveData.forecast.temperatureMax)}{unit}
                    </span>
                    <TrendingDown size={12} className="text-blue-400 ml-1" />
                    <span className="text-gray-400 text-sm">
                      {Math.round(liveData.forecast.temperatureMin)}{unit}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">{liveData.forecast.weatherLabel}</span>
                </div>
              </div>
              {forecastRangeIdx >= 0 && ranges[forecastRangeIdx] && (
                <span className="text-[10px] bg-sky-500/15 text-sky-400 px-2 py-0.5 rounded-[5px] font-medium">
                  {liveData.dataAge === "past" ? "Actual" : "Forecast"}: {ranges[forecastRangeIdx].label}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Market Ranges */}
        {ranges.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 size={14} className="text-gray-400" />
              <span className="text-xs text-gray-400 font-medium">Market Ranges</span>
            </div>
            <div className="space-y-1.5">
              {ranges.map((range, i) => {
                const price = getOutcomePrice(range.marketQuestion, markets);
                const isForecastRange = i === forecastRangeIdx;
                const isAvgRange = i === avgRangeIdx;

                return (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-3 py-2 rounded-[5px] ${
                      isForecastRange
                        ? "bg-sky-500/10 border border-sky-500/20"
                        : isAvgRange
                          ? "bg-amber-500/10 border border-amber-500/15"
                          : "bg-gray-50 dark:bg-[#18181D]"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900 dark:text-white text-sm">{range.label}</span>
                      {isForecastRange && (
                        <span className="text-[10px] bg-sky-500/15 text-sky-400 px-1.5 py-0.5 rounded font-medium">
                          {liveData?.dataAge === "past" ? "Actual" : "Forecast"}
                        </span>
                      )}
                      {isAvgRange && !isForecastRange && (
                        <span className="text-[10px] bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded font-medium">
                          Avg
                        </span>
                      )}
                    </div>
                    {price !== null && (
                      <span className={`text-sm font-bold ${
                        price >= 0.5 ? "text-emerald-400" : "text-gray-400"
                      }`}>
                        {Math.round(price * 100)}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Historical Data */}
        {historicalYears.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <History size={14} className="text-amber-400" />
                <span className="text-xs text-gray-400 font-medium">
                  Same Date, Past Years
                </span>
              </div>
              {avgMax !== null && (
                <span className="text-xs text-amber-400">
                  Avg: {avgMax}{unit}
                </span>
              )}
            </div>
            <div className="space-y-1">
              {displayedHistory.map((year) => (
                <div
                  key={year.year}
                  className="flex items-center justify-between px-3 py-1.5 bg-gray-50 dark:bg-[#18181D] rounded-[5px]"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs font-mono w-10">{year.year}</span>
                    <WeatherIcon code={year.weatherCode} className="text-gray-500" size={14} />
                    <span className="text-xs text-gray-500">{year.weatherLabel}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    <TrendingUp size={10} className="text-red-400" />
                    <span className="text-gray-900 dark:text-white font-medium">
                      {Math.round(year.temperatureMax)}{unit}
                    </span>
                    <TrendingDown size={10} className="text-blue-400" />
                    <span className="text-gray-400">
                      {Math.round(year.temperatureMin)}{unit}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {historicalYears.length > 3 && (
              <button
                onClick={() => setShowAllHistory(!showAllHistory)}
                className="flex items-center gap-1 text-xs text-sky-400 mt-1.5 hover:text-sky-300 transition-colors"
              >
                {showAllHistory ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {showAllHistory ? "Show less" : `Show all ${historicalYears.length} years`}
              </button>
            )}
          </div>
        )}

        {/* Weather data error fallback */}
        {weatherError && !liveData && (
          <div className="bg-gray-50 dark:bg-[#18181D] rounded-[5px] p-3 text-center">
            <Cloud size={20} className="text-gray-500 mx-auto mb-1" />
            <p className="text-xs text-gray-500">Weather data unavailable</p>
          </div>
        )}

        {/* Historical Note from AI */}
        {weather.historicalNote && (
          <div className="bg-gray-50 dark:bg-[#18181D] rounded-[5px] p-3">
            <p className="text-xs text-gray-400 leading-relaxed">
              {weather.historicalNote}
            </p>
          </div>
        )}

        {/* AI Reasoning */}
        {analysis.reasoning && (
          <details
            open={showReasoning}
            onToggle={(e) => setShowReasoning((e.target as HTMLDetailsElement).open)}
          >
            <summary className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer hover:text-gray-400 transition-colors list-none">
              {showReasoning ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              AI Reasoning
            </summary>
            <p className="text-xs text-gray-500 mt-2 leading-relaxed">
              {analysis.reasoning}
            </p>
          </details>
        )}
      </div>
    </div>
  );
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr + "T12:00:00");
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}
